/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useState } from 'react';
import styles from './FhirFilterAndSummaryButtonContainer.module.scss';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { DocumentVersionType } from '../algorithm/types';
import { analyticsTrack } from '../analytics_utils';
import { fhirDateFilterValues } from '../Fhir/fhir_utils';
import { setDateRange } from '../Fhir/fhirSlice';
import { selectPearlUserInfo } from '../Login/userSlice';
import { GenerateSummaryButton } from './components/PatientSummary/GenerateSummaryButton';
import RoundedColorSelect, { KeyValue } from './components/RoundedColorSelect';
import { usePatientSummary } from './hooks/ChatContext';

type FhirFilterAndSummaryButtonContainerProps = {
  documentVersion?: DocumentVersionType;
};

const FhirFilterAndSummaryButtonContainer = ({ documentVersion }: FhirFilterAndSummaryButtonContainerProps) => {
  const showPatientSummary = usePatientSummary();

  // show generate patient summary button, date filter and data disclaimer if patient summary is enabled.
  if (!showPatientSummary) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div>
        <p className={styles.headingText}>PERIOD</p>
        <DateFilterSelect documentVersion={documentVersion} />
      </div>
      <div id="summary-button" className={styles.summaryButton}>
        <p className={styles.headingText}>PATIENT SUMMARY</p>
        <GenerateSummaryButton documentVersionId={documentVersion?.id} />
      </div>
    </div>
  );
};

type DateFilterSelectProps = {
  documentVersion: DocumentVersionType | undefined;
};

const DateFilterSelect: React.FC<DateFilterSelectProps> = ({ documentVersion }) => {
  const [currDate, setCurrDate] = useState('All');
  const keyValueItems: KeyValue[] = fhirDateFilterValues.map((item) => ({
    id: item.key.toString(),
    name: item.value,
  }));
  const dispatch = useAppDispatch();
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);

  const dateChangeHandler = (val: string | undefined) => {
    if (!val) {
      return;
    }
    const date: number = fhirDateFilterValues.find((date) => date.value === val)?.key!;
    setCurrDate(val);
    dispatch(setDateRange(date));

    const selectedRange = fhirDateFilterValues.find((filter) => filter.value === val);
    const timeRange = selectedRange ? selectedRange.value : 'All';
    analyticsTrack(
      'summary_action',
      {
        action: 'date_range_filter_selected',
        time_range: timeRange,
        documentName: documentVersion?.title,
        documentSlug: documentVersion?.document.slug,
        documentId: documentVersion?.document.id,
        documentVersionId: documentVersion?.id,
        documentType: documentVersion?.document?.docType,
      },

      pearlUserInfo
    );
  };
  return (
    <RoundedColorSelect
      id="date-filter-period"
      value={currDate}
      onChange={(e) => dateChangeHandler(e?.target.value)}
      items={keyValueItems}
    />
  );
};

export default FhirFilterAndSummaryButtonContainer;
